import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from '@/api/client';
import type { Complaint, ComplaintStatus, ComplaintCategory, PageResponse } from '@/api/types';
import { AlertCircle, ChevronDown } from 'lucide-react';

const STATUS_COLORS: Record<ComplaintStatus, string> = {
  OPEN:        'bg-red-100 text-red-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-800',
  RESOLVED:    'bg-green-100 text-green-800',
  CANCELLED:   'bg-orange-100 text-orange-700',
};

const CATEGORY_LABEL_KEYS: Record<ComplaintCategory, string> = {
  ROOM:              'owner.categoryRoom',
  CORRIDOR:          'owner.categoryCorridor',
  WIFI:              'owner.categoryWifi',
  ELECTRICITY_WATER: 'owner.categoryElectricityWater',
  OTHER:             'owner.categoryOther',
};

const CATEGORY_LABELS: Record<ComplaintCategory, string> = {
  ROOM:              'Room',
  CORRIDOR:          'Corridor / Common Area',
  WIFI:              'WiFi / Internet',
  ELECTRICITY_WATER: 'Electricity / Water',
  OTHER:             'Other',
};

const STATUS_LABEL_KEYS: Record<ComplaintStatus, string> = {
  OPEN:        'complaint.open',
  IN_PROGRESS: 'complaint.inProgress',
  RESOLVED:    'complaint.resolved',
  CANCELLED:   'common.cancelled',
};

export function HostelComplaintsPage({ role }: { role: 'owner' | 'supervisor' }) {
  const { t } = useTranslation();
  const { id: hostelId } = useParams({ strict: false }) as { id: string };
  const qc = useQueryClient();

  const STATUS_OPTIONS: { value: ComplaintStatus | ''; label: string }[] = [
    { value: '',           label: t('common.all') },
    { value: 'OPEN',       label: t('complaint.open') },
    { value: 'IN_PROGRESS', label: t('complaint.inProgress') },
    { value: 'RESOLVED',   label: t('complaint.resolved') },
    { value: 'CANCELLED',  label: t('common.cancelled') },
  ];

  const [statusFilter, setStatusFilter] = useState<ComplaintStatus | ''>('');
  const [updating, setUpdating] = useState<string | null>(null);
  const [statusForm, setStatusForm] = useState<{ status: ComplaintStatus; resolutionNotes: string }>({
    status: 'IN_PROGRESS', resolutionNotes: '',
  });

  const backTo = role === 'owner'
    ? `/owner/hostels/${hostelId}` as const
    : `/supervisor/hostels/${hostelId}` as const;

  const { data, isLoading } = useQuery<PageResponse<Complaint>>({
    queryKey: ['hostel-complaints', hostelId, statusFilter],
    queryFn: () => {
      const params = new URLSearchParams({ hostelId, size: '100' });
      if (statusFilter) params.set('status', statusFilter);
      return client.get(`/api/complaints?${params}`).then(r => r as unknown as PageResponse<Complaint>);
    },
  });

  const complaints = data?.content ?? [];

  const updateMutation = useMutation({
    mutationFn: (id: string) => client.patch(`/api/complaints/${id}/status`, statusForm),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hostel-complaints', hostelId] });
      setUpdating(null);
    },
  });

  const openCount  = complaints.filter(c => c.status === 'OPEN').length;
  const inProgCount = complaints.filter(c => c.status === 'IN_PROGRESS').length;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <a href={backTo} className={`text-sm hover:underline ${role === 'owner' ? 'text-teal-600' : 'text-violet-600'}`}>← {t('owner.hostel', 'Hostel')}</a>
        <span className="text-gray-300">/</span>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <AlertCircle size={20} className="text-red-500" /> {t('complaint.title')}
        </h1>
      </div>

      {/* Summary badges */}
      <div className="flex gap-3 mb-5">
        {openCount > 0 && (
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-700">
            {t('owner.countOpen', '{{count}} Open', { count: openCount })}
          </span>
        )}
        {inProgCount > 0 && (
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800">
            {t('owner.countInProgress', '{{count}} In Progress', { count: inProgCount })}
          </span>
        )}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm text-gray-500">{t('common.filter')}:</span>
        <div className="flex gap-1.5">
          {STATUS_OPTIONS.map(o => (
            <button
              key={o.value}
              onClick={() => setStatusFilter(o.value as ComplaintStatus | '')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                statusFilter === o.value
                  ? 'bg-gray-800 text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <p className="text-gray-400 py-16 text-center">{t('common.loading')}</p>
      ) : complaints.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <AlertCircle size={36} className="text-gray-200 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">
            {statusFilter
              ? t('owner.noComplaintsWithStatus', 'No complaints with status "{{status}}".', { status: statusFilter })
              : t('complaint.noComplaints')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {complaints.map(c => (
            <div key={c.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                      {t(CATEGORY_LABEL_KEYS[c.category], CATEGORY_LABELS[c.category])}
                    </span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[c.status]}`}>
                      {t(STATUS_LABEL_KEYS[c.status], c.status.replace('_', ' '))}
                    </span>
                    {c.tenantName && (
                      <span className="text-xs text-gray-500">{t('owner.byName', 'by {{name}}', { name: c.tenantName })}</span>
                    )}
                    <span className="text-xs text-gray-400 ml-auto">
                      {new Date(c.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                  <p className="font-medium text-gray-900">{c.title}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{c.description}</p>
                  {c.resolutionNotes && (
                    <p className="text-sm text-green-700 mt-2 bg-green-50 border border-green-100 rounded px-2 py-1">
                      {t('owner.resolutionLabel', 'Resolution')}: {c.resolutionNotes}
                    </p>
                  )}
                </div>

                {c.status !== 'RESOLVED' && c.status !== 'CANCELLED' && updating !== c.id && (
                  <button
                    onClick={() => {
                      setUpdating(c.id);
                      setStatusForm({ status: 'IN_PROGRESS', resolutionNotes: '' });
                    }}
                    className={`shrink-0 text-xs font-medium border px-3 py-1.5 rounded-lg transition ${
                      role === 'owner'
                        ? 'text-teal-600 border-teal-200 hover:bg-teal-50'
                        : 'text-violet-600 border-violet-200 hover:bg-violet-50'
                    }`}
                  >
                    {t('owner.update', 'Update')} <ChevronDown size={12} className="inline" />
                  </button>
                )}
              </div>

              {/* Inline update form */}
              {updating === c.id && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                  <div className="flex gap-2">
                    {(['IN_PROGRESS', 'RESOLVED', 'CANCELLED'] as ComplaintStatus[]).map(s => (
                      <button
                        key={s}
                        onClick={() => setStatusForm(f => ({ ...f, status: s }))}
                        className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition ${
                          statusForm.status === s
                            ? 'border-gray-800 bg-gray-800 text-white'
                            : 'border-gray-200 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        {t(STATUS_LABEL_KEYS[s], s.replace('_', ' '))}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={statusForm.resolutionNotes}
                    onChange={e => setStatusForm(f => ({ ...f, resolutionNotes: e.target.value }))}
                    placeholder={statusForm.status === 'IN_PROGRESS' ? t('owner.addNoteOptional', 'Add a note (optional)…') : t('owner.resolutionNotesOptional', 'Resolution notes (optional)…')}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateMutation.mutate(c.id)}
                      disabled={updateMutation.isPending}
                      className={`text-sm text-white px-4 py-1.5 rounded-lg font-medium disabled:opacity-50 ${
                        role === 'owner' ? 'bg-teal-600 hover:bg-teal-700' : 'bg-violet-600 hover:bg-violet-700'
                      }`}
                    >
                      {updateMutation.isPending ? t('owner.saving', 'Saving…') : t('common.save')}
                    </button>
                    <button
                      onClick={() => setUpdating(null)}
                      className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5"
                    >
                      {t('common.cancel')}
                    </button>
                    {updateMutation.isError && (
                      <p className="text-red-600 text-sm self-center">
                        {(updateMutation.error as Error).message}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
